import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Target, TrendingUp, Bot, ArrowRight, CheckCircle } from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 'workflow-automation',
      icon: <Zap className="service-detail-icon" />,
      title: "Workflow Automation",
      description: "Transform your manual, time-consuming processes into seamless automated workflows.",
      features: [
        "Custom automation solutions tailored to your business",
        "Integration with existing tools and systems",
        "Automated data entry and processing",
        "Intelligent document handling and routing",
        "Real-time notifications and alerts",
        "Workflow monitoring and analytics"
      ],
      benefits: "Save up to 20 hours per week per employee while reducing errors by 95%"
    },
    {
      id: 'process-auditing',
      icon: <Target className="service-detail-icon" />,
      title: "Process Auditing",
      description: "Identify inefficiencies and uncover hidden opportunities for optimization in your operations.",
      features: [
        "Comprehensive process mapping and analysis",
        "Bottleneck identification and resolution strategies",
        "ROI analysis for automation opportunities",
        "Detailed audit reports with actionable insights",
        "Priority-based implementation roadmap",
        "Ongoing monitoring and optimization recommendations"
      ],
      benefits: "Typically uncover 40-60% efficiency improvement opportunities within the first audit"
    },
    {
      id: 'ai-consultation',
      icon: <TrendingUp className="service-detail-icon" />,
      title: "AI Consultation",
      description: "Expert guidance to navigate the AI landscape and implement solutions that drive real business value.",
      features: [
        "AI readiness assessment",
        "Custom AI strategy development",
        "Technology stack recommendations",
        "Implementation planning and support",
        "Team training and change management",
        "Ongoing optimization and scaling support"
      ],
      benefits: "Make informed decisions with confidence and avoid costly implementation mistakes"
    },
    {
      id: 'build-agent',
      icon: <Bot className="service-detail-icon" />,
      title: "Build AI Agent",
      description: "Create custom AI agents designed specifically for your unique business needs and processes.",
      features: [
        "Custom-trained AI models for your industry",
        "Natural language processing capabilities",
        "Integration with business systems and databases",
        "24/7 automated customer support",
        "Intelligent task execution and decision-making",
        "Continuous learning and improvement"
      ],
      benefits: "Deploy intelligent assistants that work around the clock to serve your customers and team"
    },
    {
      id: 'build-chatbot',
      icon: <Bot className="service-detail-icon" />,
      title: "Build Chatbot",
      description: "Deploy intelligent conversational interfaces that enhance customer experience and reduce support workload.",
      features: [
        "Custom chatbot design tailored to your brand voice",
        "Multi-channel deployment (website, WhatsApp, Slack, etc.)",
        "Natural conversation flow with contextual understanding",
        "Seamless handoff to human agents when needed",
        "Analytics dashboard for conversation insights",
        "Multilingual support for global audiences"
      ],
      benefits: "Reduce response time by 90% while handling thousands of customer inquiries simultaneously"
    }
  ];

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-content">
          <h1 className="page-title">
            Comprehensive <span className="text-accent">Automation Solutions</span>
          </h1>
          <p className="page-subtitle">
            From strategy to implementation, we provide end-to-end services that transform your business operations and drive sustainable growth.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="services-detail-section">
        {services.map((service, index) => (
          <div key={service.id} id={service.id} className={`service-detail-card ${index % 2 === 1 ? 'reverse' : ''}`}>
            <div className="service-detail-content">
              <div className="service-detail-icon-wrapper">
                {service.icon}
              </div>
              <h2 className="service-detail-title">{service.title}</h2>
              <p className="service-detail-description">{service.description}</p>
              
              <div className="service-features">
                <h3 className="features-heading">What's Included:</h3>
                <ul className="features-list">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <CheckCircle className="feature-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="service-benefit-box">
                <p className="benefit-text">{service.benefits}</p>
              </div>

              <Link to="/contact" className="btn-primary">
                Get Started
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        ))}
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="services-cta-content">
          <h2 className="services-cta-title">Not Sure Where to Start?</h2>
          <p className="services-cta-subtitle">
            Book a free consultation with our experts. We'll analyze your processes and recommend the best approach for your business.
          </p>
          <div className="services-cta-buttons">
            <Link to="/contact" className="btn-primary">
              Get Free Consultation
              <ArrowRight size={20} />
            </Link>
            <Link to="/case-studies" className="btn-secondary">
              View Case Studies
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
